import { Banner, IconButton, Spinner } from '@augmentt-dev/ui-components'
import clsx from 'clsx'
import PropTypes from 'prop-types'
import React, { useCallback, useId, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import DetailRow from '../../../../../components/DetailRow'
import { MAILBOX_DELEGATION_RIGHTS } from './constants'
import formatTrustee from './formatTrustee'
import ManageMailboxDelegationsModal from './ManageMailboxDelegationsModal/ManageMailboxDelegationsModal'
import styles from './MailboxDelegations.module.scss'
import { MAILBOX_DELEGATION_ERROR_CODES, useMailboxDelegationsState } from './useMailboxDelegations'

const ERROR_MESSAGES = {
  [MAILBOX_DELEGATION_ERROR_CODES.CONSENT_REQUIRED]: {
    id: 'ENGAGE.MAILBOX_DELEGATIONS.ERROR_CONSENT',
    defaultMessage: 'Mailbox permissions could not be loaded. Exchange admin consent is missing for this tenant.',
  },
  [MAILBOX_DELEGATION_ERROR_CODES.EXCHANGE_UNAVAILABLE]: {
    id: 'ENGAGE.MAILBOX_DELEGATIONS.ERROR_UNAVAILABLE',
    defaultMessage: 'Mailbox permissions could not be loaded because Exchange could not be reached.',
  },
}

const propTypes = {
  customerId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  employeeId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  enabled: PropTypes.bool,
  canManage: PropTypes.bool,
}

const trusteeList = (trustees) => {
  const labels = (trustees ?? []).map(formatTrustee).filter(Boolean)
  if (labels.length === 0) {
    return null
  }
  return labels.join(', ')
}

function MailboxDelegations({ customerId = null, employeeId = null, enabled = false, canManage = false }) {
  const titleId = useId()
  const intl = useIntl()
  const [showManage, setShowManage] = useState(false)
  const openManage = useCallback(() => setShowManage(true), [])
  const closeManage = useCallback(() => setShowManage(false), [])
  const { status, delegations, errorCode, pending, refreshing, refreshErrorCode, retry, applyChange } =
    useMailboxDelegationsState({
      customerId,
      employeeId,
      enabled,
    })

  if (!enabled || customerId == null || employeeId == null) {
    return null
  }

  if (status === 'ready' && delegations?.hasMailbox === false) {
    return null
  }

  const errorMessage = ERROR_MESSAGES[errorCode] ?? ERROR_MESSAGES[MAILBOX_DELEGATION_ERROR_CODES.EXCHANGE_UNAVAILABLE]
  const isLoading = status === 'loading' || status === 'idle'

  return (
    <section className={clsx('mb-4', styles.section)} aria-labelledby={titleId} aria-busy={isLoading}>
      <div className={clsx('d-flex align-items-center mb-3', styles.titleRow)}>
        <h6 id={titleId} className={clsx('text-bold mb-0', styles.title)}>
          <FormattedMessage id="ENGAGE.MAILBOX_DELEGATIONS.TITLE" defaultMessage="Mailbox permissions" />
        </h6>
        {status === 'ready' && canManage && (
          <IconButton
            icon="fas fa-pen-to-square"
            variant="ghost"
            label={intl.formatMessage({
              id: 'ENGAGE.MAILBOX_DELEGATIONS.EDIT',
              defaultMessage: 'Edit mailbox permissions',
            })}
            onClick={openManage}
          />
        )}
      </div>

      {isLoading && (
        <div className="d-flex align-items-center">
          <Spinner size="small" />
          <span className="visually-hidden">
            <FormattedMessage id="ENGAGE.MAILBOX_DELEGATIONS.LOADING" defaultMessage="Loading mailbox permissions" />
          </span>
        </div>
      )}

      {status === 'error' && (
        <Banner
          message={<FormattedMessage id={errorMessage.id} defaultMessage={errorMessage.defaultMessage} />}
          buttonText={<FormattedMessage id="GENERAL.TRY_AGAIN" defaultMessage="Try again" />}
          onPress={retry}
        />
      )}

      {status === 'ready' &&
        MAILBOX_DELEGATION_RIGHTS.map((right) => (
          <DetailRow
            key={right.key}
            size="sm"
            label={<FormattedMessage id={right.messageId} defaultMessage={right.defaultMessage} />}
            value={
              trusteeList(delegations?.[right.key]) ?? <FormattedMessage id="GENERAL.NONE" defaultMessage="None" />
            }
          />
        ))}

      <ManageMailboxDelegationsModal
        show={showManage}
        onHide={closeManage}
        customerId={customerId}
        employeeId={employeeId}
        delegations={delegations}
        pending={pending}
        refreshing={refreshing}
        refreshErrorCode={refreshErrorCode}
        onRetry={retry}
        onChange={applyChange}
      />
    </section>
  )
}

MailboxDelegations.propTypes = propTypes

export default React.memo(MailboxDelegations)
